import * as React from 'react';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import Select, { components, CSSObjectWithLabel, OptionProps, SelectInstance } from 'react-select';
import {
  Cell,
  CellTemplate,
  Compatible,
  getCellProperty,
  getCharFromKey,
  isAlphaNumericKey,
  keyCodes,
  Uncertain,
  UncertainCompatible,
} from '@silevis/reactgrid';

export type OptionType = {
  label: string;
  value: string;
  isDisabled?: boolean;
};

export interface DropdownCell extends Cell {
  type: 'dropdown';
  selectedValue?: string;
  values: OptionType[];
  isDisabled?: boolean;
  selectOpen?: string;
  selectKey?: string;
  inputValue?: string;
  styles?: {
    container?: CSSObjectWithLabel;
    control?: CSSObjectWithLabel;
    indicatorsContainer?: CSSObjectWithLabel;
    dropdownIndicator?: CSSObjectWithLabel;
    singleValue?: CSSObjectWithLabel;
    indicatorSeparator?: CSSObjectWithLabel;
    input?: CSSObjectWithLabel;
    valueContainer?: CSSObjectWithLabel;
  };
}

export class DropdownCellTemplate implements CellTemplate<DropdownCell> {
  getCompatibleCell(uncertainCell: Uncertain<DropdownCell>): Compatible<DropdownCell> {
    let selectedValue: string | undefined;

    try {
      selectedValue = getCellProperty(uncertainCell, 'selectedValue', 'string');
    } catch {
      selectedValue = undefined;
    }

    const values = getCellProperty(uncertainCell, 'values', 'object');
    const value = selectedValue ? parseFloat(selectedValue) : NaN;

    let isDisabled = true;
    try {
      isDisabled = getCellProperty(uncertainCell, 'isDisabled', 'boolean');
    } catch {
      isDisabled = false;
    }

    let inputValue: string | undefined;
    try {
      inputValue = getCellProperty(uncertainCell, 'inputValue', 'string');
    } catch {
      inputValue = undefined;
    }

    let selectOpen: string | undefined;
    try {
      selectOpen = getCellProperty(uncertainCell, 'selectOpen', 'string');
    } catch {
      selectOpen = undefined;
    }

    let selectKey: string | undefined;
    try {
      selectKey = getCellProperty(uncertainCell, 'selectKey', 'string');
    } catch {
      selectKey = undefined;
    }

    const text = selectedValue || '';

    return {
      ...uncertainCell,
      selectedValue,
      text,
      value,
      values,
      isDisabled,
      selectOpen,
      selectKey,
      inputValue,
    };
  }

  update(
    cell: Compatible<DropdownCell>,
    cellToMerge: UncertainCompatible<DropdownCell>,
  ): Compatible<DropdownCell> {
    let selectedValueFromText: string | undefined = undefined;
    const findByLabel = cell.values.find((val: any) => val.label === cellToMerge.text);

    if (findByLabel) {
      selectedValueFromText = findByLabel.value;
    }

    if (!findByLabel && cell.values.some((val: any) => val.value === cellToMerge.text)) {
      selectedValueFromText = cellToMerge.text;
    }

    return this.getCompatibleCell({
      ...cell,
      selectedValue: selectedValueFromText,
      selectOpen: cellToMerge.selectOpen,
      inputValue: cellToMerge.inputValue,
    });
  }

  getClassName(cell: Compatible<DropdownCell>, isInEditMode: boolean): string {
    const isOpen = cell.selectOpen === cell.selectKey ? 'open' : 'closed';
    return `${cell.className ? cell.className : ''}${isOpen}`;
  }

  handleKeyDown(
    cell: Compatible<DropdownCell>,
    keyCode: number,
    ctrl: boolean,
    shift: boolean,
    alt: boolean,
    key: string,
    capsLock: boolean,
  ): {
    cell: Compatible<DropdownCell>;
    enableEditMode: boolean;
  } {
    if (ctrl && keyCode === 86) {
      // Обработчик вставки по Ctrl + V
      navigator.clipboard.readText().then(pastedText => {
        const trimmedText = pastedText.trim();

        // Найти значение в списке values
        const matchedOption = cell.values.find(
          option => option.label === trimmedText || option.value === trimmedText,
        );

        if (matchedOption) {
          // Если найдено, обновляем selectedValue и закрываем select
          return {
            cell: this.getCompatibleCell({
              ...cell,
              selectedValue: matchedOption.value,
              selectOpen: undefined, // Закрываем выпадающий список
              inputValue: undefined,
            }),
            enableEditMode: false,
          };
        }
      });

      return { cell, enableEditMode: false }; // Возвращаем текущий cell до завершения
    }

    // Стандартное поведение (пробел или enter)
    if ((keyCode === keyCodes.SPACE || keyCode === keyCodes.ENTER) && !shift) {
      return {
        cell: this.getCompatibleCell({
          ...cell,
          selectOpen: cell.selectOpen === cell.selectKey ? undefined : cell.selectKey,
        }),
        enableEditMode: false,
      };
    }

    const char = getCharFromKey(key, shift, capsLock);

    if (!ctrl && !alt && isAlphaNumericKey(keyCode) && !(shift && keyCode === keyCodes.SPACE))
      return {
        cell: this.getCompatibleCell({
          ...cell,
          inputValue: char,
          selectOpen: cell.selectOpen === cell.selectKey ? undefined : cell.selectKey,
        }),
        enableEditMode: false,
      };

    return { cell, enableEditMode: false };
  }

  handleCompositionEnd(
    cell: Compatible<DropdownCell>,
    eventData: any,
  ): {
    cell: Compatible<DropdownCell>;
    enableEditMode: boolean;
  } {
    return {
      cell: {
        ...cell,
        inputValue: eventData,
        selectOpen: cell.selectOpen === cell.selectKey ? undefined : cell.selectKey,
      },
      enableEditMode: false,
    };
  }

  render(
    cell: Compatible<DropdownCell>,
    isInEditMode: boolean,
    onCellChanged: (cell: Compatible<DropdownCell>, commit: boolean) => void,
  ): React.ReactNode {
    return (
      <DropdownInput
        onCellChanged={cell => onCellChanged(this.getCompatibleCell(cell), true)}
        cell={cell}
        isInEditMode={isInEditMode}
      />
    );
  }
}

interface DIProps {
  onCellChanged: (...args: any[]) => void;
  cell: Record<string, any>;
  isInEditMode: boolean;
}

const DropdownInput: FC<DIProps> = ({ onCellChanged, cell, isInEditMode }) => {
  const { selectOpen, selectKey } = cell;
  const selectRef = useRef<SelectInstance | null>(null);
  const [inputValue, setInputValue] = useState<string | undefined>(cell.inputValue);
  const selectedValue = useMemo<OptionType | undefined>(
    () => cell.values.find((val: any) => val.value === cell.text),
    [cell.text, cell.values],
  );

  useEffect(() => {
    if (selectOpen === selectKey && selectRef.current) {
      selectRef.current.focus();
      setInputValue(cell.inputValue);
    }
    console.log(`Row index: ${cell.rowIndex}, Total rows: ${cell.totalRows}`);
    // Определение положения меню
    // if (cell.rowIndex !== undefined && cell.totalRows !== undefined) {
    //   setMenuPlacement(cell.rowIndex === cell.totalRows - 1 ? 'top' : 'bottom');
    // }
  }, [selectOpen, selectKey, cell.inputValue, cell.rowIndex, cell.totalRows]);

  return (
    <div
      style={{ width: '100%' }}
      onPointerDown={e => {
        onCellChanged({ ...cell, selectOpen: selectKey });
      }}
    >
      <Select
        {...(cell.inputValue && {
          inputValue,
          defaultInputValue: inputValue,
          onInputChange: e => setInputValue(e),
        })}
        {...(selectOpen !== undefined && { menuIsOpen: selectOpen === selectKey })}
        placeholder="Выбрать"
        isSearchable={true}
        ref={selectRef}
        components={{
          Option: CustomOption,
        }}
        onMenuClose={() =>
          onCellChanged({
            ...cell,
            selectOpen: selectOpen === selectKey ? undefined : selectKey,
            inputValue: undefined,
          })
        }
        onMenuOpen={() => {
          isInEditMode && onCellChanged({ ...cell, selectOpen: selectKey });
        }}
        onChange={e => {
          onCellChanged({
            ...cell,
            selectedValue: (e as OptionType).value,
            selectKey: undefined,
            inputValue: undefined,
          });
        }}
        menuShouldBlockScroll
        defaultValue={selectedValue}
        value={selectedValue}
        isDisabled={cell.nonEditable}
        options={cell.values}
        onKeyDown={e => {
          e.stopPropagation();
          if (e.key === 'Escape') {
            selectRef.current?.blur();
            return onCellChanged({ ...cell, selectKey: undefined, inputValue: undefined });
          }
        }}
        menuPlacement={'auto'}
        theme={theme => ({
          ...theme,
          colors: {
            ...theme.colors,
            primary: '#3D308D',
            primary25: '#302575',
            primary50: '#302575',
            neutral0: '#271E64',
          },
        })}
        styles={{
          container: provided => ({
            ...provided,
            width: '100%',
            height: '100%',
            ...cell.styles?.container,
          }),
          control: provided => ({
            ...provided,
            border: 'none',
            borderColor: 'transparent',
            minHeight: '25px',
            background: 'transparent',
            boxShadow: 'none',
            ...cell.styles?.control,
          }),
          indicatorsContainer: provided => ({
            ...provided,
            paddingTop: '0px',
            ...cell.styles?.indicatorsContainer,
          }),
          dropdownIndicator: provided => ({
            ...provided,
            padding: '0px 4px',
            ...cell.styles?.dropdownIndicator,
          }),
          singleValue: provided => ({
            ...provided,
            color: 'inherit',
            ...cell.styles?.singleValue,
          }),
          indicatorSeparator: provided => ({
            ...provided,
            marginTop: '4px',
            marginBottom: '4px',
            ...cell.styles?.indicatorSeparator,
          }),
          input: provided => ({
            ...provided,
            padding: 0,
            color: '#FFFFFF',
            ...cell.styles?.input,
          }),
          valueContainer: provided => ({
            ...provided,
            padding: '0 8px',
            ...cell.styles?.valueContainer,
          }),
          option: (provided, props) => ({
            ...provided,
            color: props.isSelected || props.isFocused ? 'white' : '#9FA8B8',
            cursor: 'pointer',
          }),
        }}
      />
    </div>
  );
};

// const CustomOption: React.FC<OptionProps<OptionType, false>> = ({
//   innerProps,
//   label,
//   isSelected,
//   isFocused,
//   isDisabled,
// }) => (
//   <div
//     {...innerProps}
//     onPointerDown={e => e.stopPropagation()}
//     className={`rg-dropdown-option${isSelected ? ' selected' : ''}${isFocused ? ' focused' : ''}${isDisabled ? ' disabled' : ''}`}
//   >
//     {label}
//   </div>
// );

// const CustomMenu: React.FC<MenuProps<OptionType, false>> = ({
//   // innerProps,
//   // innerRef,
//   // children,
//   // className,
//   // getStyles,
//   children,
//   ...props
// }) =>
//   children &&
//   React.cloneElement(children as React.ReactElement, {
//     ...props,
//     onpointerdown: e => e.stopPropagation(),
//   });

// (
//   <div
//     {...innerProps}
//     // className="rg-dropdown-menu-global"
//     ref={innerRef}
//     onPointerDown={e => e.stopPropagation()}
//     className={className}
//   >
//     {children}
//   </div>,
// );

const CustomOption = (props: OptionProps<OptionType, false>) => (
  <components.Option
    {...props}
    innerProps={{
      ...props.innerProps,
      onPointerDown: (e: React.PointerEvent) => {
        e.stopPropagation();
        if (props.innerProps.onPointerDown)
          props.innerProps.onPointerDown(e as React.PointerEvent<HTMLDivElement>);
      },
    }}
  />
);
